import Vue from "vue";
import Router from "vue-router";
import store from "_store";
import middlewarePipeline from "./_middleware/middlewarePipeline";

// Import middleware
import guest from "./_middleware/guest";
import auth from "./_middleware/auth";
import authUser from "./_middleware/authUser";
import authAdmin from "./_middleware/authAdmin";
import authSeller from "./_middleware/authSeller";
import authGU from "./_middleware/authGU";
import authUS from "./_middleware/authUS";
import authSSU from "./_middleware/authSSU";

Vue.use(Router);

// Group routes by feature/module
const authRoutes = [
    {
        path: "/login",
        name: "login",
        component: () => import(/* webpackChunkName: "auth" */ "_pages/Login/"),
        meta: {
            middleware: [guest],
        },
    },
    {
        path: "/forgot-password",
        name: "forgot-password",
        component: () =>
            import(/* webpackChunkName: "auth" */ "_pages/ForgotPassword/"),
        meta: {
            middleware: [guest],
        },
    },
    {
        path: "/forgot-password/:token",
        name: "reset-forgot-password",
        component: () =>
            import(
                /* webpackChunkName: "auth" */ "_pages/ForgotPassword/ResetPassword"
            ),
        meta: {
            middleware: [guest],
        },
    },
    {
        path: "/register",
        name: "register",
        component: () =>
            import(/* webpackChunkName: "auth" */ "_pages/Register/"),
        meta: {
            middleware: [guest],
        },
    },
    {
        path: "/register/seller",
        name: "register-seller",
        component: () =>
            import(/* webpackChunkName: "auth" */ "_pages/Register/Seller/"),
        meta: {
            middleware: [guest],
        },
    },
    {
        path: "/register/buyer",
        name: "register-buyer",
        component: () =>
            import(/* webpackChunkName: "auth" */ "_pages/Register/Buyer/"),
        meta: {
            middleware: [guest],
        },
    },
];

const homeRoutes = [
    {
        path: "",
        component: () => import(/* webpackChunkName: "home" */ "_pages/Home/"),
        meta: {
            middleware: [guest],
        },
    },
    {
        path: "/home",
        name: "home",
        component: () => import(/* webpackChunkName: "home" */ "_pages/Home/"),
        meta: {},
    },
    {
        path: "/liebe",
        name: "valentines",
        component: () =>
            import(/* webpackChunkName: "seasonal" */ "_pages/Home/Valentines"),
    },
    {
        path: "/ostern",
        name: "easter",
        component: () =>
            import(/* webpackChunkName: "seasonal" */ "_pages/Home/Easter"),
    },
    {
        path: "/muttertag",
        name: "muttertag",
        component: () =>
            import(/* webpackChunkName: "seasonal" */ "_pages/Home/Mother"),
    },
    {
        path: "/vatertag",
        name: "vatertag",
        component: () =>
            import(/* webpackChunkName: "seasonal" */ "_pages/Home/Father"),
    },
    {
        path: "/guest-wallet",
        name: "guest-wallet",
        component: () =>
            import(/* webpackChunkName: "guest" */ "_pages/Home/Guest/Wallet"),
        meta: {
            middleware: [guest],
        },
    },
];

const voucherRoutes = [
    {
        path: "/vouchers",
        name: "vouchers",
        component: () =>
            import(/* webpackChunkName: "vouchers" */ "_pages/Vouchers/"),
        meta: {
            middleware: [auth, authSeller],
        },
    },
    {
        path: "/vouchers-gift",
        name: "vouchers-gift",
        component: () =>
            import(/* webpackChunkName: "vouchers" */ "_pages/Vouchers/Gift"),
        meta: {
            middleware: [auth, authSeller, authSSU],
        },
    },
    {
        path: "/vouchers/new",
        name: "vouchers-new",
        component: () =>
            import(
                /* webpackChunkName: "vouchers-edit" */ "_pages/Vouchers/New/"
            ),
        meta: {
            back: "/vouchers",
            middleware: [auth, authSeller],
        },
    },
    {
        path: "/wallet/vouchers/new",
        name: "buyers-vouchers-new",
        component: () =>
            import(
                /* webpackChunkName: "vouchers-edit" */ "_pages/Vouchers/New/Buyer"
            ),
        meta: {
            back: "/wallet",
            middleware: [auth],
        },
    },
    {
        path: "/vouchers/update/:id",
        name: "vouchers-update",
        component: () =>
            import(
                /* webpackChunkName: "vouchers-edit" */ "_pages/Vouchers/New/"
            ),
        meta: {
            back: "/vouchers",
            middleware: [auth, authSeller],
        },
    },
    {
        path: "/vouchers/statistic/:id",
        name: "vouchers-statistic",
        component: () =>
            import(
                /* webpackChunkName: "vouchers-stats" */ "_pages/Vouchers/Statistics/"
            ),
        meta: {
            back: "/vouchers",
            middleware: [auth, authSeller],
        },
    },
    {
        path: "/vouchers/category/:slug",
        name: "vouchers-category",
        component: () =>
            import(
                /* webpackChunkName: "vouchers-browse" */ "_pages/Vouchers/Category/"
            ),
        meta: {
            middleware: [authGU],
        },
    },
    {
        path: "/vouchers/search",
        name: "vouchers-search",
        component: () =>
            import(
                /* webpackChunkName: "vouchers-browse" */ "_pages/Vouchers/Search/"
            ),
        meta: {
            middleware: [authGU],
        },
    },
    {
        path: "/vouchers/send-email/:id",
        name: "vouchers-send-email",
        component: () =>
            import(
                /* webpackChunkName: "vouchers-actions" */ "_pages/Vouchers/SendEmail/"
            ),
        meta: {
            back: "/wallet",
            middleware: [auth, authUser],
        },
    },
    {
        path: "/vouchers/transfer/:id",
        name: "vouchers-transfer",
        component: () =>
            import(
                /* webpackChunkName: "vouchers-actions" */ "_pages/Vouchers/Transfer/"
            ),
        meta: {
            back: "/wallet",
            middleware: [auth, authUser],
        },
    },
    {
        path: "/vouchers/:id",
        name: "vouchers-detail",
        component: () =>
            import(
                /* webpackChunkName: "vouchers-view" */ "_pages/Vouchers/Detail/"
            ),
        meta: {
            back: "/home",
        },
    },
    {
        path: "/voucher/:id",
        name: "voucher-detail",
        component: () =>
            import(
                /* webpackChunkName: "vouchers-view" */ "_pages/Vouchers/WalletDetail/"
            ),
        meta: {
            back: "/wallet",
        },
    },
    {
        path: "/buyer/voucher/:id",
        name: "buyer-voucher-detail",
        component: () =>
            import(
                /* webpackChunkName: "vouchers-view" */ "_pages/Vouchers/WalletDetail/BuyerVoucher"
            ),
        meta: {
            back: "/wallet",
        },
    },
    {
        path: "/vouchers/personalized/:id",
        name: "vouchers-personalized",
        component: () =>
            import(
                /* webpackChunkName: "vouchers-personalize" */ "_pages/Vouchers/Personalized/"
            ),
        meta: {
            back: "/wallet",
            middleware: [auth, authUser],
        },
    },
    {
        path: "/buyer/vouchers/personalized/:id",
        name: "buyer-vouchers-personalized",
        component: () =>
            import(
                /* webpackChunkName: "vouchers-personalize" */ "_pages/Vouchers/Personalized/BuyerVoucher"
            ),
        meta: {
            back: "/wallet",
            middleware: [auth, authUser],
        },
    },
    {
        path: "/featured-vouchers",
        name: "featured-vouchers",
        component: () =>
            import(
                /* webpackChunkName: "vouchers-admin" */ "_pages/Vouchers/ManageFeature/"
            ),
        meta: {
            middleware: [auth, authAdmin],
        },
    },
    {
        path: "/voucher-expired",
        name: "voucher-expired",
        component: () =>
            import(
                /* webpackChunkName: "vouchers-misc" */ "_pages/Vouchers/Expired"
            ),
    },
];

const profileRoutes = [
    {
        path: "/profile-info",
        name: "profile-info",
        component: () =>
            import(/* webpackChunkName: "profile" */ "_pages/Profile/Info/"),
        meta: {
            middleware: [auth, authUS],
        },
    },
    {
        path: "/website-configuration",
        name: "website-configuration",
        component: () =>
            import(
                /* webpackChunkName: "profile" */ "_pages/Profile/Info/WebConfig"
            ),
        meta: {
            middleware: [auth, authSeller],
        },
    },
    {
        path: "/profile-payment",
        name: "profile-payment",
        component: () =>
            import(/* webpackChunkName: "profile" */ "_pages/Profile/Payment/"),
        meta: {
            middleware: [auth, authUser],
        },
    },
    {
        path: "/profile-settings",
        name: "profile-settings",
        component: () =>
            import(
                /* webpackChunkName: "profile" */ "_pages/Profile/Settings/"
            ),
        meta: {
            middleware: [auth, authUS],
        },
    },
];

const sellerRoutes = [
    {
        path: "/scanner",
        name: "scanner",
        component: () =>
            import(/* webpackChunkName: "seller-tools" */ "_pages/Scanner/"),
        meta: {
            middleware: [auth, authSSU],
        },
    },
    {
        path: "/orders-earnings",
        name: "orders-earnings",
        component: () =>
            import(
                /* webpackChunkName: "seller-finance" */ "_pages/OrdersEarnings/"
            ),
        meta: {
            middleware: [auth, authSeller],
        },
    },
    {
        path: "/seller/:id",
        name: "seller-detail",
        component: () =>
            import(/* webpackChunkName: "seller" */ "_pages/Seller/Detail/"),
        meta: {
            back: "",
        },
    },
    {
        path: "/tourism/employee-vouchers",
        name: "tourism-employee-vouchers",
        component: () =>
            import(
                /* webpackChunkName: "tourism" */ "_pages/Tourism/EmployeeVouchers"
            ),
        meta: {
            middleware: [auth, authSeller],
        },
    },
    {
        path: "/tourism/employee-codes",
        name: "tourism-employee-codes",
        component: () =>
            import(
                /* webpackChunkName: "tourism" */ "_pages/Tourism/EmployeeCodes"
            ),
        meta: {
            middleware: [auth, authSeller],
        },
    },
    {
        path: "/employee-codes",
        name: "employee-codes",
        component: () =>
            import(
                /* webpackChunkName: "tourism" */ "_pages/Tourism/EmployeeCodes"
            ),
        meta: {
            middleware: [auth, authSeller],
        },
    },
    {
        path: "/tourism/sellers",
        name: "tourism-sellers",
        component: () =>
            import(/* webpackChunkName: "tourism" */ "_pages/Tourism/Partners"),
        meta: {
            middleware: [auth, authSeller],
        },
    },
];

const userRoutes = [
    {
        path: "/cart",
        name: "cart",
        component: () => import(/* webpackChunkName: "cart" */ "_pages/Cart/"),
        meta: {
            back: "",
        },
    },
    {
        path: "/vouchers-gift/:id",
        name: "vouchers-gift-details",
        component: () =>
            import(/* webpackChunkName: "cart" */ "_pages/Cart/Gift"),
        meta: {
            middleware: [auth, authSeller, authSSU],
        },
    },
    {
        path: "/wallet",
        name: "wallet",
        component: () =>
            import(/* webpackChunkName: "wallet" */ "_pages/Wallet/"),
        meta: {
            middleware: [auth, authUser],
        },
    },
    {
        path: "/wallet/vouchers",
        name: "wallet-vouchers",
        component: () =>
            import(/* webpackChunkName: "wallet" */ "_pages/Wallet/Vouchers"),
        meta: {
            middleware: [auth, authUser],
        },
    },
    {
        path: "/orders",
        name: "orders",
        component: () =>
            import(/* webpackChunkName: "orders" */ "_pages/Orders/"),
        meta: {
            middleware: [auth, authUser],
        },
    },
    {
        path: "/payment",
        name: "payment",
        component: () =>
            import(/* webpackChunkName: "payment" */ "_pages/Payment/"),
        meta: {
            middleware: [auth, authUser],
        },
    },
    {
        path: "/wish-list",
        name: "wish-list",
        component: () =>
            import(/* webpackChunkName: "user" */ "_pages/WishList"),
        meta: {
            middleware: [auth, authUser],
        },
    },
];

const adminRoutes = [
    {
        path: "/settings",
        name: "settings",
        component: () =>
            import(/* webpackChunkName: "admin" */ "_pages/Settings/"),
        meta: {
            middleware: [auth, authAdmin],
        },
    },
    {
        path: "/settings/user/:id",
        name: "settings-user",
        component: () =>
            import(/* webpackChunkName: "admin" */ "_pages/Settings/User/"),
        meta: {
            middleware: [auth, authAdmin],
        },
    },
    {
        path: "/category",
        name: "category",
        component: () =>
            import(/* webpackChunkName: "admin-catalog" */ "_pages/Category/"),
        meta: {
            middleware: [auth, authAdmin],
        },
    },
    {
        path: "/sub-category",
        name: "sub-category",
        component: () =>
            import(
                /* webpackChunkName: "admin-catalog" */ "_pages/SubCategory/"
            ),
        meta: {
            middleware: [auth, authAdmin],
        },
    },
    {
        path: "/subdomain",
        name: "subdomain",
        component: () =>
            import(
                /* webpackChunkName: "admin" */ "_pages/Subdomain/AdminSubdomain"
            ),
        meta: {
            middleware: [auth, authAdmin],
        },
    },
    {
        path: "/payout",
        name: "payout",
        component: () =>
            import(/* webpackChunkName: "admin-finance" */ "_pages/Payout/"),
        meta: {
            middleware: [auth, authAdmin],
        },
    },
    {
        path: "/template",
        name: "template",
        component: () =>
            import(/* webpackChunkName: "admin" */ "_pages/Template/"),
        meta: {
            middleware: [auth, authAdmin],
        },
    },
    {
        path: "/users",
        name: "users",
        component: () =>
            import(/* webpackChunkName: "admin-users" */ "_pages/Users/"),
        meta: {
            middleware: [auth, authAdmin],
        },
    },
    {
        path: "/manage-orders",
        name: "manage-orders",
        component: () =>
            import(
                /* webpackChunkName: "admin-orders" */ "_pages/Orders/Manage/"
            ),
        meta: {
            middleware: [auth, authAdmin],
        },
    },
    {
        path: "/voucher-redemptions",
        name: "voucher-redemptions",
        component: () =>
            import(/* webpackChunkName: "admin" */ "_pages/Redemptions/"),
        meta: {
            middleware: [auth, authAdmin],
        },
    },
];

const miscRoutes = [
    {
        path: "/ein-liebevolles-geschenk/:id",
        name: "transfer",
        component: () =>
            import(/* webpackChunkName: "transfers" */ "_pages/Transfer/"),
    },
    {
        path: "/transfer/:id",
        redirect: { name: "transfer" },
    },
    {
        path: "/buyer-voucher/transfer/:id",
        name: "buyer-transfer",
        component: () =>
            import(
                /* webpackChunkName: "transfers" */ "_pages/Transfer/BuyerVoucher"
            ),
    },
    {
        path: "/gast-bestellung/abrufen",
        component: () =>
            import(/* webpackChunkName: "guest" */ "_pages/GuestVoucher"),
    },
    {
        path: "/wp",
        component: () =>
            import(/* webpackChunkName: "integrations" */ "_pages/Plugin"),
    },
    {
        path: "/wp/tourism/partners",
        component: () =>
            import(
                /* webpackChunkName: "integrations" */ "_pages/Tourism/PartnersWordPress"
            ),
    },
    {
        path: "/survey/test",
        component: () =>
            import(/* webpackChunkName: "misc" */ "_pages/Survey/"),
    },
    {
        path: "/.well-known/*",
    },
    {
        path: "*",
        name: "404",
        component: () =>
            import(
                /* webpackChunkName: "errors" */ "_pages/Errors/PageNotFound"
            ),
        meta: {
            middleware: [auth],
        },
    },
    {
        path: "/error/not-allowed",
        name: "not-allowed",
        component: () =>
            import(
                /* webpackChunkName: "errors" */ "_pages/Errors/UserNotAllowed"
            ),
        meta: {
            middleware: [auth],
        },
    },
];

// Combine all route groups
const routes = [
    ...authRoutes,
    ...homeRoutes,
    ...voucherRoutes,
    ...profileRoutes,
    ...sellerRoutes,
    ...userRoutes,
    ...adminRoutes,
    ...miscRoutes,
];

const router = new Router({
    mode: "history",
    base: process.env.BASE_URL,
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        } else {
            return { x: 0, y: 0 };
        }
    },
    routes,
});

router.beforeEach((to, from, next) => {
    if (!to.meta.middleware) {
        return next();
    }
    const middleware = to.meta.middleware;

    const context = {
        to,
        from,
        next,
        router,
        store,
    };

    return middleware[0]({
        ...context,
        next: middlewarePipeline(context, middleware, 1),
    });
});

export default router;
